import React, { useRef, useState } from 'react';
import { Modal, Input, Button, Table, Typography, Space, App, Segmented, Tag, Tooltip, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

const { Text } = Typography;

type ImportFormat = 'text' | 'json';

interface ParsedServer {
  line: number;
  name: string;
  mainIP: string;
  networks: string;
  proxiesCount: number;
  type: string;
  metaKeys?: string[]; // top-level keys from source-provided meta, for preview
  error?: string;
}

const VALID_TYPES = ['', 'dedicated', 'mobile-gateway'];

interface Props {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const VALID_PROTOCOLS = ['http', 'https', 'socks5'];

function parseImportText(text: string): ParsedServer[] {
  const lines = text.split('\n');
  const results: ParsedServer[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parts = trimmed.split('|');
    if (parts.length < 2) {
      results.push({ line: i + 1, name: '', mainIP: '', networks: '', proxiesCount: 0, type: '', error: 'Need at least name|mainIP' });
      return;
    }

    const name = parts[0]?.trim() || '';
    const mainIP = parts[1]?.trim() || '';
    const networks = parts[2]?.trim() || '';
    const type = parts[3]?.trim() || '';

    let error: string | undefined;
    if (!name) error = 'Empty name';
    else if (!mainIP) error = 'Empty IP';
    else if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(mainIP)) error = 'Invalid IP format';
    else if (type && !VALID_TYPES.includes(type)) error = `Invalid type "${type}"`;

    results.push({ line: i + 1, name, mainIP, networks, proxiesCount: 0, type: type || 'dedicated', error });
  });

  return results;
}

function parseImportJSON(text: string): ParsedServer[] {
  const results: ParsedServer[] = [];

  let entries: any[];
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) {
      return [{ line: 1, name: '', mainIP: '', networks: '', proxiesCount: 0, type: '', error: 'JSON must be an array' }];
    }
    entries = parsed;
  } catch (e: any) {
    return [{ line: 1, name: '', mainIP: '', networks: '', proxiesCount: 0, type: '', error: `Invalid JSON: ${e.message}` }];
  }

  entries.forEach((entry, i) => {
    const idx = i + 1;
    const name = entry.name || '';
    const mainIP = entry.mainIP || '';
    const networks = Array.isArray(entry.networks) ? entry.networks.join(', ') : '';
    const proxies = Array.isArray(entry.proxies) ? entry.proxies : [];
    const type = entry.type || '';

    let error: string | undefined;
    if (!name) {
      error = 'name is required';
    } else if (!mainIP) {
      error = 'mainIP is required';
    } else if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(mainIP)) {
      error = 'Invalid IP format';
    } else if (type && !VALID_TYPES.includes(type)) {
      error = `invalid type "${type}"`;
    } else {
      // Validate proxies
      for (let j = 0; j < proxies.length; j++) {
        const p = proxies[j];
        if (!p.protocol || !VALID_PROTOCOLS.includes(p.protocol)) {
          error = `proxy ${j + 1}: invalid protocol`;
          break;
        }
        if (!p.host) {
          error = `proxy ${j + 1}: host required`;
          break;
        }
        if (!p.port || p.port < 1 || p.port > 65535) {
          error = `proxy ${j + 1}: invalid port`;
          break;
        }
      }
    }

    const metaKeys = entry.meta && typeof entry.meta === 'object' && !Array.isArray(entry.meta)
      ? Object.keys(entry.meta)
      : undefined;
    results.push({ line: idx, name, mainIP, networks, proxiesCount: proxies.length, type: type || 'dedicated', metaKeys, error });
  });

  return results;
}

const JSON_PLACEHOLDER = `[
  {
    "name": "proxy-us-1",
    "mainIP": "10.0.0.1",
    "type": "dedicated",
    "networks": ["192.168.1.0/24", "10.0.1.0/24"],
    "tags": ["production"],
    "sourceKey": "sysboard",
    "sourceId": "sb-12345",
    "sourceUrl": "https://sysboard.example.com/server/12345",
    "meta": { "inventoryId": "abc-42", "owner": "team-proxies" },
    "proxies": [
      { "protocol": "http", "host": "10.0.0.1", "port": 8080 },
      { "protocol": "socks5", "host": "10.0.0.1", "port": 1080, "login": "user", "password": "pass" }
    ],
    "proxyMetas": {
      "10.0.0.1:1080": { "vendor": "Squid" }
    }
  },
  {
    "name": "mobile-gw-1",
    "mainIP": "10.0.0.2",
    "type": "mobile-gateway",
    "proxies": [
      { "protocol": "socks5", "host": "10.0.0.2", "port": 1080, "label": "Orange SIM 1" },
      { "protocol": "socks5", "host": "10.0.0.2", "port": 1081, "label": "T-Mobile SIM 2" }
    ]
  }
]`;

const ImportModal: React.FC<Props> = ({ open, onCancel, onSuccess }) => {
  const { message } = App.useApp();
  const [format, setFormat] = useState<ImportFormat>('json');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<ParsedServer[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    updated: number;
    errors: string[];
    untouched?: {
      fromSource: { key: string; name: string; sourceId?: string }[];
      fromOther: { key: string; name: string }[];
      sourceKey: string;
    };
  } | null>(null);
  const [deletingSource, setDeletingSource] = useState(false);
  const [deletingOther, setDeletingOther] = useState(false);

  const handlePreview = () => {
    const parsed = format === 'json' ? parseImportJSON(text) : parseImportText(text);
    setPreview(parsed);
    setShowPreview(true);
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const isJSON = format === 'json';
      const payload = isJSON ? JSON.parse(text) : text;
      const headers = isJSON
        ? { 'Content-Type': 'application/json' }
        : { 'Content-Type': 'text/plain' };

      const { data } = await apiClient.post('/servers/import', payload, { headers });
      setResult(data.data);
      const parts = [];
      if (data.data.created) parts.push(`${data.data.created} created`);
      if (data.data.updated) parts.push(`${data.data.updated} updated`);
      message.success(`Import: ${parts.join(', ') || '0 servers'}`);
      onSuccess();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setText('');
    setPreview([]);
    setShowPreview(false);
    setResult(null);
    onCancel();
  };

  const handleFormatChange = (value: ImportFormat) => {
    setFormat(value);
    setShowPreview(false);
  };

  const validCount = preview.filter((p) => !p.error).length;
  const errorCount = preview.filter((p) => p.error).length;

  const typeTag = (t: string) => {
    // Plain text: a server's type is not a status, so it gets no colour.
    return <Text>{t === 'mobile-gateway' ? 'Mobile' : 'Dedicated'}</Text>;
  };

  const textColumns = [
    { title: '#', dataIndex: 'line', key: 'line', width: 48, align: 'right' as const },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Main IP', dataIndex: 'mainIP', key: 'mainIP' },
    { title: 'Networks', dataIndex: 'networks', key: 'networks' },
    {
      title: 'Type', dataIndex: 'type', key: 'type', width: 100,
      render: (t: string) => typeTag(t),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: ParsedServer) =>
        record.error ? <Text type="danger">{record.error}</Text> : <Text type="success">OK</Text>,
    },
  ];

  const jsonColumns = [
    { title: '#', dataIndex: 'line', key: 'line', width: 48, align: 'right' as const },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Main IP', dataIndex: 'mainIP', key: 'mainIP', width: 130 },
    {
      title: 'Type', dataIndex: 'type', key: 'type', width: 100,
      render: (t: string) => typeTag(t),
    },
    {
      title: 'Networks', dataIndex: 'networks', key: 'networks',
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">--</Text>,
    },
    {
      title: 'Proxies', key: 'proxies', width: 72, align: 'right' as const,
      render: (_: any, r: ParsedServer) => r.proxiesCount > 0
        ? r.proxiesCount
        : <Text type="secondary">--</Text>,
    },
    {
      title: 'Meta', key: 'meta', width: 88,
      render: (_: any, r: ParsedServer) => r.metaKeys && r.metaKeys.length > 0
        ? <Tooltip title={r.metaKeys.join(', ')}><Tag style={{ margin: 0 }}>{r.metaKeys.length} keys</Tag></Tooltip>
        : <Text type="secondary">--</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 160,
      render: (_: any, record: ParsedServer) =>
        record.error
          ? <Tooltip title={record.error}><Text type="danger" ellipsis>{record.error}</Text></Tooltip>
          : <Text type="success">OK</Text>,
    },
  ];

  return (
    <Modal
      title="Import Servers"
      open={open}
      onCancel={handleClose}
      width={900}
      destroyOnHidden
      // Actions live in the footer so they stay put when the preview table
      // appears underneath the textarea.
      footer={result
        ? [<Button key="close" type="primary" onClick={handleClose}>Close</Button>]
        : [
          <Button key="cancel" onClick={handleClose}>Cancel</Button>,
          <Button key="preview" onClick={handlePreview} disabled={!text.trim()}>Preview</Button>,
          <Button key="import" type="primary" onClick={handleImport} loading={loading} disabled={!text.trim()}>
            Import
          </Button>,
        ]}
    >
      {result ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>Created: {result.created}, Updated: {result.updated || 0}</Text>
          {result.errors?.length > 0 && (
            <div>
              <Text type="danger">Errors:</Text>
              <ul>
                {result.errors.map((e, i) => (
                  <li key={i}><Text type="danger">{e}</Text></li>
                ))}
              </ul>
            </div>
          )}
          {result.untouched && result.untouched.fromSource?.length > 0 && (
            <div>
              <Text type="warning">
                {result.untouched.fromSource.length} server{result.untouched.fromSource.length > 1 ? 's' : ''} from
                &lsquo;{result.untouched.sourceKey}&rsquo; not in import:
              </Text>
              <Table
                dataSource={result.untouched.fromSource}
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ y: 200 }}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  { title: 'Source ID', dataIndex: 'sourceId', key: 'sourceId', ellipsis: true },
                  { title: 'Key', dataIndex: 'key', key: 'key', ellipsis: true },
                ]}
                style={{ marginTop: 8 }}
              />
              <Button
                danger
                loading={deletingSource}
                style={{ marginTop: 8 }}
                onClick={async () => {
                  setDeletingSource(true);
                  try {
                    const keys = result.untouched!.fromSource.map((s) => s.key);
                    await apiClient.post('/servers/bulk-delete', { keys });
                    message.success(`Deleted ${keys.length} server${keys.length > 1 ? 's' : ''} from ${result.untouched!.sourceKey}`);
                    setResult({ ...result, untouched: { ...result.untouched!, fromSource: [] } });
                    onSuccess();
                  } catch (err: any) {
                    message.error(err.response?.data?.error || 'Delete failed');
                  } finally {
                    setDeletingSource(false);
                  }
                }}
              >
                Delete {result.untouched.fromSource.length} untouched from {result.untouched.sourceKey}
              </Button>
            </div>
          )}
          {result.untouched && result.untouched.fromOther?.length > 0 && (
            <div>
              <Text type="warning">
                {result.untouched.fromOther.length} other server{result.untouched.fromOther.length > 1 ? 's' : ''} not in import:
              </Text>
              <Table
                dataSource={result.untouched.fromOther}
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ y: 200 }}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  { title: 'Key', dataIndex: 'key', key: 'key', ellipsis: true },
                ]}
                style={{ marginTop: 8 }}
              />
              <Button
                danger
                loading={deletingOther}
                style={{ marginTop: 8 }}
                onClick={async () => {
                  setDeletingOther(true);
                  try {
                    const keys = result.untouched!.fromOther.map((s) => s.key);
                    await apiClient.post('/servers/bulk-delete', { keys });
                    message.success(`Deleted ${keys.length} server${keys.length > 1 ? 's' : ''}`);
                    setResult({ ...result, untouched: { ...result.untouched!, fromOther: [] } });
                    onSuccess();
                  } catch (err: any) {
                    message.error(err.response?.data?.error || 'Delete failed');
                  } finally {
                    setDeletingOther(false);
                  }
                }}
              >
                Delete {result.untouched.fromOther.length} other untouched server{result.untouched.fromOther.length > 1 ? 's' : ''}
              </Button>
            </div>
          )}
        </Space>
      ) : (
        <>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Space>
              <Text type="secondary">Format:</Text>
              <Segmented
                options={[
                  { label: 'JSON', value: 'json' },
                  { label: 'Text', value: 'text' },
                ]}
                value={format}
                onChange={(v) => handleFormatChange(v as ImportFormat)}
              />
            </Space>

            <Text type="secondary" style={{ display: 'block' }}>
              {format === 'json'
                ? 'JSON array of server objects. Supports type ("dedicated"|"mobile-gateway"), networks, proxies, tags, checkMode, priority, sourceKey/sourceId/sourceUrl. Country is detected automatically.'
                : 'Format: name|mainIP|network1,network2|type (one server per line). 4th column optional — defaults to "dedicated". Country is detected automatically.'}
            </Text>

            <Upload.Dragger
              accept={format === 'json' ? '.json' : '.txt,.csv'}
              showUploadList={false}
              beforeUpload={(file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const content = e.target?.result as string;
                  if (content) {
                    setText(content);
                    setShowPreview(false);
                  }
                };
                reader.readAsText(file);
                return false; // prevent auto upload
              }}
              style={{ padding: 0 }}
            >
              <Space>
                <UploadOutlined />
                <Text type="secondary">Drop a {format === 'json' ? '.json' : '.txt'} file here or click to browse</Text>
              </Space>
            </Upload.Dragger>

            <Input.TextArea
              rows={showPreview ? 6 : 10}
              value={text}
              onChange={(e) => { setText(e.target.value); setShowPreview(false); }}
              placeholder={format === 'json' ? JSON_PLACEHOLDER : 'proxy-us-1|10.0.0.1|192.168.1.0/24\nproxy-eu-1|10.0.0.2|10.0.1.0/24,10.0.2.0/24'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />

            {showPreview && (
              <div>
                <Space style={{ marginBottom: 8 }}>
                  <Text type="success">{validCount} valid</Text>
                  {errorCount > 0 && <Text type="danger">{errorCount} errors</Text>}
                </Space>
                <Table
                  dataSource={preview}
                  columns={format === 'json' ? jsonColumns : textColumns}
                  rowKey="line"
                  size="small"
                  pagination={false}
                  scroll={{ y: 300 }}
                />
              </div>
            )}

          </Space>
        </>
      )}
    </Modal>
  );
};

export default ImportModal;
